import { AuthGuard, roleGuard } from "@dis/auth";
import { CollectionSelectionService } from "../layout/collection-detail-layout/collection-selection.service";
import { provideDetailLinks } from "../layout/collection-detail-layout/detail-links.service";
import { JtmsRoutes, collectionDetailLayout, tablesLayout } from "../core/routing/route-data";

export default [
  {
    path: "",
    canMatch: [AuthGuard],
    data: { navGroup: "operations" },
    children: [
      {
        path: "",
        pathMatch: "full",
        data: { navOrder: 0 },
        loadComponent: () =>
          import("../feature/dashboards/shipment-by-status/shipment-by-status.component").then(
            (m) => m.ShipmentByStatus
          ),
        title: "Overview",
      },
      {
        path: "jobs",
        children: [
          {
            path: "",
            pathMatch: "full",
            loadComponent: tablesLayout,
            children: [
              {
                path: "",
                data: { navOrder: 1 },
                loadComponent: () => import("../feature/tables/jobs/jobs-table.component").then((m) => m.JobsTable),
                title: "Jobs",
              },
            ],
          },
          {
            path: "schedule",
            loadComponent: () => import("../charts/jobs-gantt/jobs-gantt.component").then((m) => m.JobsGanttChart),
            title: "Jobs Schedule",
          },
          {
            path: "testing",
            data: { navOrder: 2 },
            loadComponent: () =>
              import("../feature/dashboards/in-house-testing-jobs/in-house-testing-jobs/in-house-testing-jobs.component").then(
                (m) => m.InHouseTestingJobsDashboard
              ),
            title: "Testing Jobs",
          },
          {
            path: "returned",
            loadComponent: () =>
              import("../feature/shop/ship-n-receiving/return-tools/return-tools-container/return-tools-container.component").then(
                (m) => m.ReturnToolsContainer
              ),
            title: "Returned Jobs",
          },
          {
            path: ":jobId",
            providers: [CollectionSelectionService, provideDetailLinks("entity")],
            loadComponent: collectionDetailLayout,
            children: [
              {
                path: "",
                data: { entityType: "job" },
                loadComponent: () =>
                  import("../feature/dashboards/collection-detail/views/job-detail-view-container/job-detail-view-container.component").then(
                    (m) => m.JobDetailViewContainer
                  ),
                title: "Job Detail",
              },
            ],
          },
        ],
      },
      {
        // Same gantt/table shape as the legacy /bha-sections routes, so the pages' ../table and ../gantt toggles work.
        path: "well-schedule",
        children: [
          { path: "", pathMatch: "full", redirectTo: "gantt" },
          {
            path: "gantt",
            data: { navOrder: 3, castable: true },
            loadComponent: () =>
              import("../feature/bha-sections/bha-section-schedule/bha-section-schedule").then(
                (m) => m.BhaSectionSchedule
              ),
            title: "Well Schedule",
          },
          {
            path: "table",
            loadComponent: () =>
              import("../feature/bha-sections/bha-section-table/bha-section-table.component").then(
                (m) => m.BhaSectionTable
              ),
            title: "Well Sections",
          },
        ],
      },
      {
        path: "builds",
        children: [
          {
            path: "",
            pathMatch: "full",
            loadComponent: tablesLayout,
            children: [
              {
                path: "",
                data: { navOrder: 4 },
                loadComponent: () =>
                  import("../feature/tables/builds/builds-table.component").then((m) => m.BuildsTable),
                title: "Builds",
              },
            ],
          },
          {
            path: "staged",
            data: { navOrder: 5 },
            loadComponent: () =>
              import("../feature/dashboards/staged-tools/staged-tools-container/staged-tools-container.component").then(
                (m) => m.StagedToolsContainer
              ),
            title: "Staged Builds",
          },
          {
            // v2 assembler; v1 (/assembler/new, /assembler/:buildId) stays at its old URL until v2 has parity.
            path: "new",
            loadComponent: () =>
              import("../feature/assembler/assembler-start/assembler-start.component").then((m) => m.AssemblerStart),
            title: "New Build",
          },
          {
            path: ":buildId",
            loadComponent: () =>
              import("../feature/assembler/assembler-build/assembler-build.component").then(
                (m) => m.AssemblerBuildPage
              ),
            title: "Build",
          },
        ],
      },
      {
        path: "shipments",
        children: [
          {
            path: "",
            pathMatch: "full",
            loadComponent: tablesLayout,
            children: [
              {
                path: "",
                data: { navOrder: 6 },
                loadComponent: () =>
                  import("../feature/tables/shipments/shipments-table.component").then((m) => m.ShipmentsTable),
                title: "Shipments",
              },
            ],
          },
          {
            // Admin-only, as at /settings/vessels; revisit with the group guards.
            path: "vessels",
            data: { navOrder: 7, adminOnly: true },
            canActivate: [roleGuard(["admin", "super_admin"])],
            loadComponent: () =>
              import("../feature/settings/vessels/vessels-list/vessels-list.component").then(
                (m) => m.VesselsListComponent
              ),
            title: "Tracked Vessels",
          },
          {
            path: ":shipmentId",
            providers: [CollectionSelectionService, provideDetailLinks("entity")],
            loadComponent: collectionDetailLayout,
            children: [
              {
                path: "",
                data: { entityType: "shipment" },
                loadComponent: () =>
                  import("../feature/dashboards/collection-detail/views/shipment-detail-view-container/shipment-detail-view-container.component").then(
                    (m) => m.ShipmentDetailViewContainer
                  ),
                title: "Shipment Detail",
              },
            ],
          },
        ],
      },
      {
        path: "rigs",
        canActivate: [roleGuard(["viewer", "editor", "admin", "super_admin"])],
        loadComponent: tablesLayout,
        children: [
          {
            path: "",
            data: { navOrder: 8 },
            loadComponent: () => import("../feature/rigs/rigs-ag-grid/rigs-ag-grid").then((m) => m.RigsAgGrid),
            title: "Rigs",
          },
        ],
      },
      {
        path: "reports/run-counts",
        data: { navOrder: 9, report: true, reportOwner: "operations", alsoIn: ["billing"] },
        loadComponent: () =>
          import("../feature/dashboards/tool-run-counts/run-counts-dashboard").then((m) => m.RunCountsDashboard),
        title: "Run Counts",
      },
      {
        path: "tools",
        data: { navOrder: 10 },
        loadComponent: () => import("../feature/tools/tools-placeholder").then((m) => m.ToolsPlaceholder),
        title: "Tools",
      },
    ],
  },
] satisfies JtmsRoutes;
